import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { TableSkeleton } from '../../Components/common/Skeleton';
import Pagination from '../../Components/common/Pagination';

const renderSurgeonName = (surgery) => {
  if (surgery.surgeon?.user?.firstName) {
    return `Dr. ${surgery.surgeon.user.firstName} ${surgery.surgeon.user.lastName}`;
  }
  if (surgery.lead?.surgeonType === 'other' && surgery.lead?.assignedSurgeonOtherName) {
    return `${surgery.lead.assignedSurgeonOtherName} (External)`;
  }
  return 'Not Assigned';
};

const renderAnesthesiologistName = (surgery) => {
  if (surgery.anesthesiologist?.user?.firstName) {
    return `Dr. ${surgery.anesthesiologist.user.firstName} ${surgery.anesthesiologist.user.lastName}`;
  }
  const extName = surgery.lead?.assignedanesthesiologistOtherName || surgery.lead?.assignedAnesthesiologistOtherName;
  if (surgery.lead?.anesthesiologistType === 'other' && extName) {
    return `${extName} (External)`;
  }
  return 'Not Assigned';
};

const SurgeryList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPage = parseInt(searchParams.get('page')) || 1;
  const tab = searchParams.get('tab') || 'active';
  const [surgeries, setSurgeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    startDate: '',
    endDate: '',
    doctorId: '',
    hospitalId: ''
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [pagination, setPagination] = useState({
    page: urlPage,
    limit: 10,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    if (pagination.page !== urlPage) {
      setPagination(prev => ({ ...prev, page: urlPage }));
    }
  }, [urlPage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchTerm }));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPagination(prev => ({ ...prev, page: urlPage }));
  }, [tab]);

  useEffect(() => {
    fetchSurgeries();
  }, [pagination.page, filters, tab]);

  useEffect(() => {
    fetchAuxiliaryData();
  }, []);

  const fetchAuxiliaryData = async () => {
    try {
      const [docRes, hospRes] = await Promise.all([
        api.get('/doctors?limit=100'),
        api.get('/hospitals?limit=100')
      ]);
      setDoctors(docRes.data.data.doctors || []);
      setHospitals(hospRes.data.data.hospitals || []);
    } catch (error) {
      console.error('Failed to load filter data');
    }
  };

  const fetchSurgeries = async () => {
    try {
      if (surgeries.length === 0) {
        setLoading(true);
      } else {
        setIsFetching(true);
      }
      const apiStatus = filters.status || tab;
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        sortBy: 'updatedAt',
        sortOrder: 'desc',
        ...(apiStatus && { status: apiStatus }),
        ...(filters.search && { search: filters.search }),
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
        ...(filters.doctorId && { doctorId: filters.doctorId }),
        ...(filters.hospitalId && { hospitalId: filters.hospitalId })
      });

      const response = await api.get(`/surgeries?${params}`);
      setSurgeries(response.data.data.surgeries || []);
      setPagination((prev) => ({
        ...prev,
        total: response.data.data.pagination.total || 0,
        pages: response.data.data.pagination.pages || 0
      }));
    } catch (error) {
      toast.error('Failed to load surgeries');
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === 'search') {
      setSearchTerm(value);
    } else {
      setFilters((prev) => ({ ...prev, [name]: value }));
      setSearchParams(prev => {
        prev.delete('page');
        return prev;
      });
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilters({
      status: '',
      search: '',
      startDate: '',
      endDate: '',
      doctorId: '',
      hospitalId: ''
    });
    setSearchParams(prev => {
      prev.delete('page');
      return prev;
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setSearchParams(prev => {
      prev.set('page', newPage.toString());
      return prev;
    });
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const getStageLabel = (status, leadStatus) => {
    const activeStatus = leadStatus || status || 'new';
    const stages = {
      new: { num: 1, label: 'Intake' },
      lead_captured: { num: 1, label: 'Intake' },
      screening: { num: 2, label: 'Screening' },
      pre_screening: { num: 2, label: 'Screening' },
      patient_ready: { num: 3, label: 'Readiness' },
      doc_verification: { num: 4, label: 'Doctor Review' },
      under_review: { num: 4, label: 'Doctor Review' },
      doctor_review: { num: 4, label: 'Doctor Review' },
      clinical_review: { num: 4, label: 'Doctor Review' },
      clinical_approval: { num: 5, label: 'Surgeon Assignment' },
      surgery_ready: { num: 5, label: 'Surgeon Assignment' },
      ready_for_surgery: { num: 5, label: 'Surgeon Assignment' },
      hospital_assignment: { num: 6, label: 'OT Schedule' },
      consultation_scheduled: { num: 6, label: 'OT Schedule' },
      scheduling: { num: 7, label: 'Payment Links' },
      scheduled: { num: 7, label: 'Payment Links' },
      assigned: { num: 7, label: 'Payment Links' },
      payment_pending: { num: 8, label: 'Payment Pending' },
      paid: { num: 9, label: 'Paid / Confirmed' },
      payment_completed: { num: 9, label: 'Paid / Confirmed' },
      surgery: { num: 10, label: 'Live Surgery' },
      surgery_started: { num: 10, label: 'Live Surgery' },
      in_surgery: { num: 10, label: 'Live Surgery' },
      closure: { num: 11, label: 'Post-Op' },
      surgery_completed: { num: 11, label: 'Post-Op' },
      follow_up_completed: { num: 11, label: 'Post-Op' },
      payout_processing: { num: 12, label: 'Payout' },
      payout_generated: { num: 12, label: 'Payout' },
      completed: { num: 13, label: 'Finalized' },
      closed: { num: 13, label: 'Finalized' },
      cancelled: { num: 0, label: 'Cancelled' },
      postponed: { num: 0, label: 'Postponed' },
      not_interested: { num: 0, label: 'Not Interested' },
      lost: { num: 0, label: 'Lost' },
      doctor_rejected: { num: 0, label: 'Doctor Rejected' }
    };
    const stage = stages[activeStatus] || {
      num: 1,
      label: (activeStatus || 'NEW').replace(/_/g, ' ').toUpperCase()
    };
    return (
      <div className="flex flex-col">
        <span className="text-[10px] font-black text-indigo-600 uppercase tracking-tighter">Stage {stage.num}</span>
        <span className="text-[10px] font-bold text-slate-500 uppercase">{stage.label}</span>
      </div>
    );
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-50 text-blue-700 border border-blue-100',
      screening: 'bg-indigo-50 text-indigo-700 border border-indigo-100',
      patient_ready: 'bg-indigo-50 text-indigo-700 border border-indigo-100',
      doc_verification: 'bg-purple-50 text-purple-700 border border-purple-100',
      clinical_approval: 'bg-violet-50 text-violet-700 border border-violet-100',
      surgery_ready: 'bg-teal-50 text-teal-700 border border-teal-100',
      hospital_assignment: 'bg-cyan-50 text-cyan-700 border border-cyan-100',
      scheduling: 'bg-sky-50 text-sky-700 border border-sky-100',
      assigned: 'bg-blue-50 text-blue-700 border border-blue-100',
      payment_pending: 'bg-amber-50 text-amber-700 border border-amber-100',
      paid: 'bg-emerald-50 text-emerald-700 border border-emerald-100',
      payment_completed: 'bg-emerald-50 text-emerald-700 border border-emerald-100',
      surgery: 'bg-orange-50 text-orange-700 border border-orange-100',
      surgery_started: 'bg-indigo-50 text-indigo-700 border border-indigo-100',
      surgery_completed: 'bg-purple-50 text-purple-700 border border-purple-100',
      follow_up_completed: 'bg-pink-50 text-pink-700 border border-pink-100',
      payout_processing: 'bg-emerald-50 text-emerald-700 border border-emerald-100',
      payout_generated: 'bg-cyan-50 text-cyan-700 border border-cyan-100',
      closed: 'bg-slate-50 text-slate-700 border border-slate-100',
      completed: 'bg-slate-50 text-slate-700 border border-slate-100',
      cancelled: 'bg-red-50 text-red-700 border border-red-100',
      postponed: 'bg-gray-50 text-gray-700 border border-gray-100'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="h-8 w-32 bg-slate-200 animate-pulse rounded-lg"></div>
        </div>
        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-8">
          <TableSkeleton rows={8} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" style={{ minHeight: '80vh' }}>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          {tab === 'lost' ? 'Lost Surgical Cases' : 'Active Surgical Cases'}
        </h1>
        <button
          onClick={clearFilters}
          className="text-xs font-black text-slate-400 hover:text-indigo-600 uppercase tracking-widest transition-colors"
        >
          Reset All Filters
        </button>
      </div>

      {/* Filters Container */}
      <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/60 border border-slate-100 p-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
          <div className="md:col-span-5 space-y-1">
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Search Database</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
              <input
                type="text"
                name="search"
                value={searchTerm}
                onChange={handleFilterChange}
                placeholder="Search by Patient, ID, or Surgery Type..."
                className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-sm font-medium"
              />
            </div>
          </div>

          <div className="md:col-span-3 space-y-1">
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Live Status</label>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-sm font-medium appearance-none"
            >
              <option value="">All Statuses</option>
              <option value="assigned">Assigned / Scheduled</option>
              <option value="payment_pending">Payment Pending</option>
              <option value="payment_completed">Payment Completed</option>
              <option value="surgery_started">In Progress (Surgery Started)</option>
              <option value="surgery_completed">Completed (Surgery Done)</option>
              <option value="follow_up_completed">Follow-up Completed</option>
              <option value="payout_generated">Payout Generated</option>
              <option value="closed">Closed / Deal Done</option>
              <option value="cancelled">Cancelled</option>
              <option value="postponed">Postponed</option>
            </select>
          </div>

          <div className="md:col-span-4 flex gap-2">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className={`flex-1 px-4 py-3 rounded-2xl border transition-all text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2 ${showAdvanced ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-200' : 'bg-white border-slate-200 text-slate-600 hover:border-indigo-300'}`}
            >
              <span>⚙️</span> {showAdvanced ? 'Hide Filters' : 'Advanced Filters'}
            </button>
          </div>
        </div>

        {/* Advanced Filters Panel */}
        {showAdvanced && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-6 border-t border-slate-50 animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="space-y-1">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Start Date</label>
              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-medium"
              />
            </div>
            <div className="space-y-1">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">End Date</label>
              <input
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-medium"
              />
            </div>
            <div className="space-y-1">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Select Hospital</label>
              <select
                name="hospitalId"
                value={filters.hospitalId}
                onChange={handleFilterChange}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-medium"
              >
                <option value="">All Hospitals</option>
                {hospitals.map(h => (
                  <option key={h._id} value={h._id}>{h.hospitalName}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Select Surgeon</label>
              <select
                name="doctorId"
                value={filters.doctorId}
                onChange={handleFilterChange}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-medium"
              >
                <option value="">All Surgeons</option>
                {doctors.map(d => (
                  <option key={d._id} value={d._id}>Dr. {d.user?.firstName} {d.user?.lastName}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Surgeries Content */}
      <div 
        className={`bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transition-opacity duration-300 ${isFetching ? 'opacity-40 pointer-events-none' : 'opacity-100'}`} 
      >
        {/* Mobile Card View */}
        <div className="block lg:hidden divide-y divide-gray-100">
          {surgeries.length > 0 ? (
            surgeries.map((surgery) => (
              <div key={surgery._id} className="p-5 space-y-4 hover:bg-gray-50 transition-colors">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-sm font-bold text-gray-900 leading-tight">{surgery.surgeryName}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{surgery.surgeryType}</div>
                  </div>
                  <div className={`px-2.5 py-1 text-[10px] font-bold rounded-full ${getStatusColor(surgery.lead?.status || surgery.status)} flex flex-col items-end gap-1`}>
                    <span>{(surgery.lead?.status || surgery.status || 'new').replace('_', ' ')}</span>
                    <span className="text-[8px] opacity-60 font-mono tracking-tighter">
                      {surgery.lead?.leadId ? `#${surgery.lead.leadId}` : (surgery.lead?.bookingId ? `#${surgery.lead.bookingId}` : 'N/A')}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Patient</p>
                    <p className="text-xs font-semibold text-gray-700">{surgery.lead?.patientName || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Schedule</p>
                    <p className="text-xs font-semibold text-gray-700">{new Date(surgery.scheduledDate).toLocaleDateString()}</p>
                    <p className="text-[10px] text-gray-500">{surgery.scheduledTime}</p>
                    <p className="text-[9px] font-black text-indigo-400 uppercase mt-1">
                      Lead: {surgery.lead?.createdAt ? `${new Date(surgery.lead.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} @ ${new Date(surgery.lead.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : 'N/A'}
                    </p>
                  </div>
                </div>

                <div className="pt-2 border-t border-gray-50">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Surgical Team</p>
                  <div className="space-y-1.5">
                    <div className="text-[11px] font-bold text-slate-700 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                      Surgeon: <span className="font-medium text-slate-600">
                        {renderSurgeonName(surgery)}
                      </span>
                    </div>
                    <div className="text-[11px] font-bold text-slate-700 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-300"></span>
                      anesthesiologist: <span className="font-medium text-slate-500">
                        {renderAnesthesiologistName(surgery)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="pt-2">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Hospital</p>
                  <p className="text-xs font-semibold text-gray-700">{surgery.hospital?.hospitalName || 'N/A'}</p>
                </div>

                {tab === 'lost' && (
                  <div className="pt-2 bg-rose-50/70 p-3 rounded-xl border border-rose-100">
                    <p className="text-[10px] font-black text-rose-500 uppercase tracking-wider mb-0.5">Reason for Loss</p>
                    <p className="text-xs font-bold text-rose-800 italic">
                      "{surgery.lead?.closedReason || surgery.cancellationReason || surgery.notes || 'Not Interested'}"
                    </p>
                  </div>
                )}

                <Link
                  to={`/leads/${surgery.lead?._id || surgery.lead || surgery._id}?from=surgeries&origin=surgeries&menu=surgeries&page=${pagination.page}`}
                  className="block w-full text-center py-3.5 bg-blue-600 text-white text-xs font-bold rounded-xl shadow-lg shadow-blue-100 active:scale-[0.98] transition-all"
                >
                  View Full Details
                </Link>
              </div>
            ))
          ) : (
            <div className="p-10 text-center text-gray-400 italic text-sm">
              No surgeries found
            </div>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="sticky left-0 bg-gray-50 z-10 px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest border-r border-gray-200">
                  Patient
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Surgery
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Doctor
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Hospital
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Lead & Booking
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Stage
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest text-nowrap">
                  Scheduled Date
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Lead Date
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Status
                </th>
                {tab === 'lost' && (
                  <th className="px-6 py-4 text-left text-[10px] font-black text-rose-500 uppercase tracking-widest">
                    Reason for Loss
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {surgeries.length > 0 ? (
                surgeries.map((surgery) => (
                  <tr
                    key={surgery._id}
                    onClick={(e) => {
                      if (e.target.closest('a') || e.target.closest('button') || e.target.closest('select')) {
                        return;
                      }
                      const targetLeadId = surgery.lead?._id || surgery.lead || surgery._id;
                      navigate(`/leads/${targetLeadId}?from=surgeries&origin=surgeries&menu=surgeries&page=${pagination.page}`);
                    }}
                    className="group hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <td className="sticky left-0 bg-white group-hover:bg-gray-50 z-10 transition-colors px-6 py-4 whitespace-nowrap border-r border-gray-200">
                      <span
                        className="text-sm text-indigo-600 font-bold hover:text-indigo-800 hover:underline"
                      >
                        {surgery.lead?.patientName || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-bold text-gray-900">{surgery.surgeryName}</div>
                        <div className="text-xs text-gray-500">{surgery.surgeryType}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-[11px] font-bold text-slate-800">
                        Surgeon: {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && surgery.surgeon?._id ? (
                          <Link to={`/doctors/${surgery.surgeon._id}`} className="font-medium text-indigo-600 hover:underline">
                            {renderSurgeonName(surgery)}
                          </Link>
                        ) : (
                          <span className="font-medium text-slate-600">
                            {renderSurgeonName(surgery)}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-1">
                        anesthesiologist: {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') && surgery.anesthesiologist?._id ? (
                          <Link to={`/doctors/${surgery.anesthesiologist._id}`} className="font-medium text-indigo-600 hover:underline">
                            {renderAnesthesiologistName(surgery)}
                          </Link>
                        ) : (
                          <span className="font-medium text-slate-600">
                            {renderAnesthesiologistName(surgery)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(user?.role === 'admin' || user?.role === 'customer_care' || user?.role === 'cc_head') ? (
                        <Link to={`/hospitals/${surgery.hospital?._id}`} className="text-sm text-indigo-600 font-medium hover:underline">
                          {surgery.hospital?.hospitalName || 'N/A'}
                        </Link>
                      ) : (
                        <div className="text-sm text-gray-600">{surgery.hospital?.hospitalName || 'N/A'}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <div className="text-[10px] font-black text-indigo-600 font-mono">
                          {surgery.lead?.leadId ? `#${surgery.lead.leadId}` : (surgery.lead?.bookingId ? `#${surgery.lead.bookingId}` : 'N/A')}
                        </div>
                        <div className="text-[9px] font-bold text-slate-400 font-mono mt-0.5">
                          BKG: {surgery.bookingId || 'PENDING'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStageLabel(surgery.status, surgery.lead?.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {new Date(surgery.scheduledDate).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-gray-500">{surgery.scheduledTime}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-[10px] font-bold text-slate-700">
                        {surgery.lead?.createdAt ? new Date(surgery.lead.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : 'N/A'}
                      </div>
                      <div className="text-[9px] font-medium text-slate-400 mt-0.5">
                        {surgery.lead?.createdAt ? new Date(surgery.lead.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2.5 py-1 text-[10px] font-bold rounded-full ${getStatusColor(surgery.lead?.status || surgery.status)}`}>
                        {(surgery.lead?.status || surgery.status || 'new').replace('_', ' ')}
                      </span>
                    </td>
                    {tab === 'lost' && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-3 py-1.5 bg-rose-50 border border-rose-100 rounded-xl text-xs font-bold text-rose-700 max-w-[220px] truncate inline-block shadow-sm">
                          "{surgery.lead?.closedReason || surgery.cancellationReason || surgery.notes || 'Not Interested'}"
                        </span>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={tab === 'lost' ? 10 : 9} className="px-6 py-10 text-center text-gray-400 italic text-sm">
                    No surgeries found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-4">
          <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
            Displaying {pagination.total > 0 ? (pagination.page - 1) * pagination.limit + 1 : 0}-{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} records
          </div>
          <div className="w-full sm:w-auto overflow-x-auto pb-2 sm:pb-0">
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.pages}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      )}
    </div>

  );
};

export default SurgeryList;
